import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel, RowsSkeleton } from "@/components/app/kit";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { FormFields, LinesEditor } from "@/components/fin/kit";

const SERIES = { invoice: "Factures", credit_note: "Avoirs", receipt: "Reçus", purchase_order: "Bons de commande", claim: "Bordereaux tiers payants", entry: "Écritures comptables" };
const KINDS = [["cash", "Espèces"], ["card", "Carte"], ["bank", "Banque"], ["mobile", "Mobile money"], ["cheque", "Chèque"], ["other", "Autre"]];

export default function FinanceSettings() {
  const { can } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/t/finance/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <RowsSkeleton />;
  const ro = !can("settings:manage");
  const setNum = (k, field, v) => setS({ ...s, numbering: { ...s.numbering, [k]: { ...s.numbering[k], [field]: v } } });
  const save = async () => {
    try {
      const body = Object.fromEntries(["numbering", "payment_terms_days", "payment_methods", "four_eyes", "valuation_method", "accounting_framework", "invoice_footer"].map((k) => [k, s[k]]));
      setS((await api.put("/t/finance/settings", body)).data);
      toast.success("Paramètres financiers enregistrés");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="space-y-5" data-testid="finance-settings">
      <Panel title={`Devise du cabinet : ${s.currency} (${s.decimals} décimale${s.decimals > 1 ? "s" : ""}, arrondi au plus proche)`}>
        <FormFields value={s} onChange={setS} prefix="fs" fields={[
          { k: "payment_terms_days", label: "Délai de paiement par défaut (jours)", type: "number", readOnly: ro },
          { k: "four_eyes", label: "Séparation des tâches (l'auteur ne peut pas valider ses propres achats/dépenses)", type: "bool", readOnly: ro },
          { k: "valuation_method", label: "Valorisation des stocks", type: "select", options: [["cmp", "Coût moyen pondéré"], ["fifo", "Premier entré, premier sorti (FIFO)"]], readOnly: ro },
          { k: "accounting_framework", label: "Référentiel comptable", type: "select", options: Object.entries(s.frameworks), readOnly: ro, hint: "Non modifiable une fois des écritures passées" },
          { k: "invoice_footer", label: "Mentions légales en pied de facture", type: "textarea", wide: true, readOnly: ro },
        ]} />
      </Panel>
      <Panel title="Numérotation contrôlée"><div className="space-y-2">
        <p className="text-xs text-slate-500">Variables : {"{prefix}"}, {"{year}"} (remise à zéro annuelle), {"{seq:05d}"}. Le numéro n'est attribué qu'à l'émission, sans trou ni doublon.</p>
        {Object.entries(SERIES).map(([k, l]) => (
          <div key={k} className="grid grid-cols-[180px_100px_1fr] gap-2 items-center text-sm">
            <span>{l}</span><Input value={s.numbering[k].prefix} disabled={ro} onChange={(e) => setNum(k, "prefix", e.target.value)} className="h-8" data-testid={`num-${k}-prefix`} />
            <Input value={s.numbering[k].pattern} disabled={ro} onChange={(e) => setNum(k, "pattern", e.target.value)} className="h-8 font-mono" data-testid={`num-${k}-pattern`} />
          </div>))}
      </div></Panel>
      <Panel title="Moyens de paiement">
        <LinesEditor readOnly={ro} testid="pm" value={s.payment_methods} onChange={(v) => setS({ ...s, payment_methods: v })} blank={() => ({ code: "", label: "", kind: "other" })}
          cols={[{ k: "code", label: "Code" }, { k: "label", label: "Libellé" }, { k: "kind", label: "Nature", type: "select", options: KINDS }]} />
      </Panel>
      <Panel title="Activité pharmaceutique">
        <p className="text-sm text-slate-600" data-testid="pharmacy-status">{s.pharmacy_authorization.authorized ? `Activité pharmaceutique autorisée (réf. ${s.pharmacy_authorization.reference}).` : "Stock interne du cabinet uniquement : la vente de médicaments au public est bloquée. Seul l'administrateur de la plateforme peut enregistrer une autorisation d'exercice pharmaceutique."}</p>
      </Panel>
      {!ro && <Button onClick={save} data-testid="finance-settings-save">Enregistrer</Button>}
    </div>
  );
}
